import { createPortal } from 'preact/compat';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Input } from './input';
import { ScrollArea } from './scroll-area';
import { IconChevronDown, IconSearch, IconClose } from './icons';
import { cn } from '../../lib/utils';
import { getCanvasScale, onCanvasScaleChange } from '../../utils/uiScale';
import { t } from '../../localization/t';
import {
  GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED,
  DIALOG_BG, DIALOG_BORDER, BEVELED_BTN, INPUT_STYLE,
} from '../../lib/medievalTheme';

// shadcn-style multi-select combobox with a search input, adapted to
// the medieval palette. Modeled on `ui/select.jsx` so both primitives
// share the same popover geometry and keyboard affordances. The only
// structural differences are that:
//   - `value` is an array, not a single string
//   - clicking an option toggles it without closing the menu
//   - the trigger shows "N selected" plus a quick-clear affordance

const MENU_VIEWPORT_PADDING = 16;
const MENU_VERTICAL_OFFSET = 8;

const TRIGGER_STYLE = {
  ...BEVELED_BTN,
  borderRadius: 'var(--radius-md)',
  color: TEXT_PRIMARY,
  backgroundColor: '#0e0a06',
};

// Menu padding (the `p-1` on the surface) and the radius of everything
// sitting one padding-step inside it (search field, option rows). The menu's
// own radius is concentric with those: outer = inner + pad. Numbers match
// the sidebar context menu (`ui/context-menu.jsx`) so both surfaces read as
// the same component family.
const MENU_PAD = '0.25cqw';
const INNER_RADIUS = 'var(--radius-lg)';
const MENU_RADIUS = `calc(${INNER_RADIUS} + ${MENU_PAD})`;

const MENU_STYLE = {
  background: DIALOG_BG,
  backgroundColor: '#0e0a06',
  border: `1px solid ${DIALOG_BORDER}`,
  borderRadius: MENU_RADIUS,
  boxShadow: '0 12px 32px rgba(0,0,0,0.6), 0 0 24px rgba(180,140,60,0.08)',
  color: TEXT_BODY,
  // cqw/cqh-based so the menu scales with the canvas. A fixed pixel cap
  // shrinks to a couple of rows on large monitors. 28cqw comfortably
  // fits the search bar plus 8+ option rows.
  maxHeight: 'min(28cqw, calc(100cqh - 40px))',
  display: 'flex',
  flexDirection: 'column',
};

const SEARCH_STYLE = {
  ...INPUT_STYLE,
  backgroundColor: '#0e0a06',
  borderRadius: INNER_RADIUS,
  color: TEXT_PRIMARY,
  height: '2cqw',
};

const OPTION_BASE_STYLE = {
  borderRadius: INNER_RADIUS,
  color: TEXT_BODY,
  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
  transition: 'background-color 120ms ease, color 120ms ease',
};

const OPTION_ACTIVE_STYLE = {
  ...OPTION_BASE_STYLE,
  background: `${GOLD} 0.18)`,
  color: TEXT_PRIMARY,
  border: `1px solid ${GOLD} 0.4)`,
};

const OPTION_HIGHLIGHTED_STYLE = {
  ...OPTION_BASE_STYLE,
  background: `${GOLD} 0.1)`,
  color: TEXT_PRIMARY,
};

function matchesOptionSearch(option, query) {
  const normalizedQuery = String(query || '').trim().toLowerCase();
  if (!normalizedQuery) return true;
  return [option?.label, option?.description, option?.value]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(normalizedQuery));
}

function MultiSelect({
  ariaLabel,
  className,
  disabled = false,
  menuAlign = 'start',
  menuClassName,
  menuPreferredWidth = null,
  menuSearchPlaceholder = t('Search…'),
  noOptionsMessage = t('No options'),
  onValueChange = () => {},
  options = [],
  placeholder = t('Select…'),
  portalMenu = false,
  triggerClassName,
  triggerHeight = 36,
  // Inline overrides for the trigger box. TRIGGER_STYLE is inline and
  // always beats a class, so callers need this to restyle it.
  triggerStyle,
  value = [],
}) {
  const [open, setOpen] = useState(false);
  const [menuQuery, setMenuQuery] = useState('');
  const [menuPosition, setMenuPosition] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  // Numeric props (menuPreferredWidth, triggerHeight) are design-pixels;
  // scale them with the canvas so the dropdown matches the surrounding UI.
  const [canvasScale, setCanvasScale] = useState(getCanvasScale());
  useEffect(() => onCanvasScaleChange(setCanvasScale), []);
  const scaledPreferredWidth = menuPreferredWidth != null ? menuPreferredWidth * canvasScale : null;
  const scaledTriggerHeight = triggerHeight * canvasScale;
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const optionRefs = useRef([]);

  const selectedSet = new Set(value || []);
  const filteredOptions = options.filter((o) => matchesOptionSearch(o, menuQuery));

  const toggleOption = (optionValue) => {
    const next = new Set(selectedSet);
    if (next.has(optionValue)) next.delete(optionValue);
    else next.add(optionValue);
    onValueChange(Array.from(next));
  };

  const clearAll = () => {
    if (selectedSet.size === 0) return;
    onValueChange([]);
  };

  function updateMenuPosition() {
    if (!portalMenu || typeof window === 'undefined') return;
    const trigger = rootRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;

    const triggerRect = trigger.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const availableWidth = Math.max(0, window.innerWidth - MENU_VIEWPORT_PADDING * 2);
    const desiredWidth = Math.max(
      triggerRect.width,
      scaledPreferredWidth ?? menuRect.width ?? triggerRect.width,
    );
    const width = Math.min(desiredWidth, availableWidth);
    const preferredLeft = menuAlign === 'end' ? triggerRect.right - width : triggerRect.left;
    const left = Math.max(
      MENU_VIEWPORT_PADDING,
      Math.min(preferredLeft, window.innerWidth - MENU_VIEWPORT_PADDING - width),
    );
    const measuredHeight = menuRect.height || menu.offsetHeight || 0;
    const spaceBelow = Math.max(
      0,
      window.innerHeight - triggerRect.bottom - MENU_VIEWPORT_PADDING - MENU_VERTICAL_OFFSET,
    );
    const spaceAbove = Math.max(0, triggerRect.top - MENU_VIEWPORT_PADDING - MENU_VERTICAL_OFFSET);
    const shouldOpenUpward = measuredHeight > 0 && spaceBelow < measuredHeight && spaceAbove > spaceBelow;
    const preferredTop = shouldOpenUpward
      ? triggerRect.top - MENU_VERTICAL_OFFSET - measuredHeight
      : triggerRect.bottom + MENU_VERTICAL_OFFSET;
    const top = measuredHeight > 0
      ? Math.max(
          MENU_VIEWPORT_PADDING,
          Math.min(preferredTop, window.innerHeight - MENU_VIEWPORT_PADDING - measuredHeight),
        )
      : Math.max(
          MENU_VIEWPORT_PADDING,
          Math.min(preferredTop, window.innerHeight - MENU_VIEWPORT_PADDING),
        );

    setMenuPosition({ left, top, width });
  }

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      if (rootRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    }
    function handleKeyDown(event) {
      if (event.key === 'Escape') setOpen(false);
    }
    function handleViewportChange() {
      updateMenuPosition();
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    if (portalMenu) {
      window.addEventListener('resize', handleViewportChange);
      window.addEventListener('scroll', handleViewportChange, true);
    }
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      if (portalMenu) {
        window.removeEventListener('resize', handleViewportChange);
        window.removeEventListener('scroll', handleViewportChange, true);
      }
    };
  }, [open, portalMenu]);

  useEffect(() => {
    if (!open) {
      setMenuQuery('');
      setMenuPosition(null);
      setHighlightedIndex(0);
    }
  }, [open]);

  useEffect(() => {
    setHighlightedIndex((idx) =>
      filteredOptions.length === 0 ? 0 : Math.min(idx, filteredOptions.length - 1),
    );
  }, [filteredOptions.length, menuQuery]);

  useEffect(() => {
    if (!open) return;
    const el = optionRefs.current[highlightedIndex];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex, open]);

  useEffect(() => {
    if (!open || !portalMenu) return undefined;
    updateMenuPosition();
    return undefined;
  }, [filteredOptions.length, menuAlign, menuPreferredWidth, open, portalMenu]);

  const selectedCount = selectedSet.size;
  const triggerLabel = selectedCount === 0
    ? placeholder
    : selectedCount === 1
      ? (options.find((o) => o.value === Array.from(selectedSet)[0])?.label || '1 selected')
      : `${selectedCount} selected`;

  const menuContent = open ? (
    <div
      ref={menuRef}
      role="listbox"
      aria-multiselectable="true"
      aria-label={ariaLabel}
      className={cn(
        'vh-menu-in',
        portalMenu
          ? 'fixed z-[140] overflow-hidden p-1'
          : 'absolute left-0 top-full z-30 mt-2 w-full overflow-hidden p-1',
        menuClassName,
      )}
      style={
        portalMenu
          ? {
              ...MENU_STYLE,
              left: `${menuPosition?.left ?? MENU_VIEWPORT_PADDING}px`,
              top: `${menuPosition?.top ?? MENU_VIEWPORT_PADDING}px`,
              width: `${menuPosition?.width ?? scaledPreferredWidth ?? 0}px`,
              visibility: menuPosition ? 'visible' : 'hidden',
            }
          : MENU_STYLE
      }
    >
      <div className="mb-1 flex items-center gap-1.5">
        <label className="relative block flex-1">
          <IconSearch
            className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2"
            style={{ color: TEXT_MUTED }}
          />
          <Input
            autoFocus
            type="search"
            role="searchbox"
            aria-label={t('Search {label}', { label: ariaLabel })}
            value={menuQuery}
            onInput={(event) => setMenuQuery(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                if (filteredOptions.length === 0) return;
                setHighlightedIndex((idx) => (idx + 1) % filteredOptions.length);
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                if (filteredOptions.length === 0) return;
                setHighlightedIndex((idx) => (idx - 1 + filteredOptions.length) % filteredOptions.length);
              } else if (event.key === 'Enter') {
                if (filteredOptions.length === 0) return;
                event.preventDefault();
                const safeIdx = Math.min(highlightedIndex, filteredOptions.length - 1);
                const choice = filteredOptions[safeIdx];
                if (choice) toggleOption(choice.value);
              }
            }}
            placeholder={menuSearchPlaceholder}
            className="pl-9 shadow-none border-0"
            style={SEARCH_STYLE}
          />
        </label>
        {selectedCount > 0 ? (
          <button
            type="button"
            className="shrink-0 px-2 h-8 text-[0.625cqw] font-semibold cursor-pointer transition-all"
            style={{
              ...BEVELED_BTN,
              borderRadius: 'var(--radius-sm)',
              color: TEXT_MUTED,
            }}
            onClick={(e) => {
              e.stopPropagation();
              clearAll();
            }}
            aria-label={t('Clear selection')}
          >
            {t('Clear')}
          </button>
        ) : null}
      </div>

      {/*
        -mr-1 pulls the scroll area into the menu's right padding so the
        overlay thumb lives in that gutter; pr-1 on the viewport keeps
        the option rows' insets symmetric.
      */}
      <ScrollArea className="flex-1 -mr-1" viewportClassName="flex flex-col gap-1 pr-1">
        {filteredOptions.length === 0 ? (
          <div className="px-1.5 py-1.5 text-xs" style={{ color: TEXT_MUTED }}>{noOptionsMessage}</div>
        ) : null}

        {(() => {
          // Renders a group header above the first option of each `group`.
          // Headers are skipped when the option list is unsorted (which is
          // the consumer's responsibility. Pre-sort by group for clean
          // visual separation).
          let lastGroup = null;
          const elements = [];
          filteredOptions.forEach((option, optionIdx) => {
            const isActive = selectedSet.has(option.value);
            const isHighlighted = optionIdx === Math.min(highlightedIndex, filteredOptions.length - 1);
            const optionStyle = isActive
              ? OPTION_ACTIVE_STYLE
              : isHighlighted
                ? OPTION_HIGHLIGHTED_STYLE
                : OPTION_BASE_STYLE;
            if (option.group && option.group !== lastGroup) {
              elements.push(
                <div
                  key={`group-${option.group}`}
                  role="presentation"
                  className="px-1.5 pt-1.5 pb-0.5 text-[0.5625cqw] font-bold"
                  style={{ color: '#715a2c' }}
                >
                  {option.group}
                </div>,
              );
              lastGroup = option.group;
            }
            elements.push(
              <button
                key={option.value}
                ref={(el) => { optionRefs.current[optionIdx] = el; }}
                type="button"
                role="option"
                aria-selected={isActive}
                data-highlighted={isHighlighted ? 'true' : undefined}
                className="flex w-full min-w-0 items-center gap-2 px-1.5 py-1.5 text-left text-xs font-medium cursor-pointer"
                style={optionStyle}
                onMouseEnter={() => setHighlightedIndex(optionIdx)}
                onClick={() => toggleOption(option.value)}
              >
                <span
                  className="flex size-4 shrink-0 items-center justify-center rounded-sm"
                  style={{
                    border: `1px solid ${isActive ? ACCENT_GOLD : 'rgba(180,140,60,0.35)'}`,
                    background: isActive ? `${GOLD} 0.25)` : 'rgba(0,0,0,0.4)',
                    color: isActive ? ACCENT_GOLD : 'transparent',
                  }}
                >
                  <svg viewBox="0 0 14 14" fill="none" style={{ width: '0.625cqw', height: '0.625cqw' }}>
                    <path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
              </button>,
            );
          });
          return elements;
        })()}
      </ScrollArea>
    </div>
  ) : null;

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        className={cn(
          'flex w-full items-center justify-between gap-2 px-1.5 text-sm font-normal cursor-pointer transition-all',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          triggerClassName,
        )}
        style={{ ...TRIGGER_STYLE, height: `${scaledTriggerHeight}px`, ...triggerStyle }}
        onClick={() => setOpen((current) => !current)}
      >
        <span
          className="flex min-w-0 items-center gap-1.5"
          style={{ color: selectedCount > 0 ? TEXT_PRIMARY : TEXT_MUTED }}
        >
          <span className="truncate">{triggerLabel}</span>
          {selectedCount > 1 ? (
            <span
              className="shrink-0 text-[0.625cqw] font-bold px-1.5 py-0.5 rounded-full"
              style={{
                background: `${GOLD} 0.25)`,
                color: ACCENT_GOLD,
                border: `1px solid ${GOLD} 0.4)`,
              }}
            >
              {selectedCount}
            </span>
          ) : null}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {selectedCount > 0 ? (
            <span
              role="button"
              tabIndex={0}
              aria-label={t('Clear selection')}
              className="flex items-center justify-center size-4 rounded cursor-pointer"
              style={{ color: TEXT_MUTED }}
              onClick={(e) => {
                e.stopPropagation();
                clearAll();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  clearAll();
                }
              }}
            >
              <IconClose className="size-3" />
            </span>
          ) : null}
          <IconChevronDown
            className={cn('size-4 transition-transform', open && 'rotate-180')}
            style={{ color: ACCENT_GOLD }}
          />
        </span>
      </button>

      {portalMenu && menuContent && typeof document !== 'undefined'
        ? createPortal(menuContent, document.body)
        : menuContent}
    </div>
  );
}

export { MultiSelect };
